// File Location: src/pages/leadGenerator/LeadGeneratorHospitals.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Users,
  Bed,
  Eye,
  Search,
  Filter,
  RefreshCw,
  TrendingUp,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Activity,
  Star,
  Award,
  ExternalLink,
  Download,
  Heart,
  Loader,
  FileText,
  Globe,
  Copy,
  Link
} from 'lucide-react';
import { 
  getHospitalsByReferrer,
  getLeadGeneratorReferralStats 
} from '../../services/hospitalService';
import { useAuth } from '../../context/AuthContext';

const LeadGeneratorHospitals = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  
  const [hospitals, setHospitals] = useState([]);
  const [filteredHospitals, setFilteredHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [showReferralLinkModal, setShowReferralLinkModal] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (userData?.leadGeneratorId) {
      loadHospitals();
      loadStats();
    }
  }, [userData]);

  useEffect(() => {
    filterHospitals();
  }, [hospitals, searchTerm, selectedStatus]);

  const loadHospitals = async () => {
    setLoading(true);
    try {
      const result = await getHospitalsByReferrer(userData.leadGeneratorId);
      if (result.success) {
        setHospitals(result.hospitals);
      } else {
        console.error('Error loading hospitals:', result.error);
      }
    } catch (error) {
      console.error('Error loading hospitals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getLeadGeneratorReferralStats(userData.leadGeneratorId);
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const filterHospitals = () => {
    let filtered = [...hospitals];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(hospital => 
        hospital.hospitalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hospital.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hospital.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hospital.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(hospital => hospital.status === selectedStatus);
    }

    setFilteredHospitals(filtered);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        label: 'Pending Review'
      },
      approved: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800 border-green-200',
        label: 'Approved & Active'
      },
      rejected: {
        icon: XCircle,
        className: 'bg-red-100 text-red-800 border-red-200',
        label: 'Rejected'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.className}`}>
        <IconComponent className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openHospitalDetails = (hospital) => {
    setSelectedHospital(hospital);
    setShowHospitalModal(true);
  };

  const generateReferralLink = () => {
    const baseUrl = window.location.origin;
    const referrerId = userData.leadGeneratorId || userData.uid;
    const link = `${baseUrl}/hospital-registration?ref=${referrerId}`;
    setReferralLink(link);
    setShowReferralLinkModal(true);
    setLinkCopied(false);
  };

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading your referred hospitals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Referred Hospitals</h1>
              <p className="text-gray-600 mt-1">Track and manage hospitals you've referred to our platform</p>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={generateReferralLink}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all shadow-sm"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Onboard New Hospital
              </button>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg font-medium transition-colors ${
                  showFilters ? 'bg-teal-50 text-teal-700 border-teal-300' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
              
              <button
                onClick={loadHospitals}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Referrals</p>
                <p className="text-3xl font-bold text-gray-900">{stats.hospitalReferrals || hospitals.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-sm text-gray-600">Hospitals referred by you</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Approved</p>
                <p className="text-3xl font-bold text-green-600">
                  {stats.approvedReferrals || hospitals.filter(h => h.status === 'approved').length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-sm text-green-600">Successfully onboarded</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {stats.pendingReferrals || hospitals.filter(h => h.status === 'pending').length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-sm text-yellow-600">Under review</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Success Rate</p>
                <p className="text-3xl font-bold text-purple-600">
                  {hospitals.length > 0 
                    ? Math.round((hospitals.filter(h => h.status === 'approved').length / hospitals.length) * 100)
                    : 0
                  }%
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-sm text-purple-600">Approval rate</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Hospitals</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Search by hospital name, city..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Hospitals Grid */}
        {filteredHospitals.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-6" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {hospitals.length === 0 ? 'No Hospitals Referred Yet' : 'No Hospitals Found'}
            </h3>
            <p className="text-gray-600 mb-6">
              {hospitals.length === 0 
                ? 'Start referring hospitals to earn commissions and grow your network'
                : 'Try adjusting your search or filter criteria'
              }
            </p>
            {hospitals.length === 0 && (
              <button
                onClick={generateReferralLink}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
              >
                <Building2 className="h-5 w-5 mr-2" />
                Onboard Your First Hospital
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHospitals.map((hospital) => (
              <div
                key={hospital.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openHospitalDetails(hospital)}
              >
                {/* Hospital Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                      {hospital.hospitalName}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{hospital.city}, {hospital.state}</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    {getStatusBadge(hospital.status)}
                  </div>
                </div>

                {/* Hospital Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Bed className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{hospital.numberOfBeds} Beds</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                    <span>Referred: {formatDate(hospital.referral?.referredAt || hospital.submittedAt)}</span>
                  </div>

                  {hospital.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="truncate">{hospital.email}</span>
                    </div>
                  )}
                </div>

                {/* Specialities */}
                {hospital.specialities && hospital.specialities.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Specialities</p>
                    <div className="flex flex-wrap gap-1">
                      {hospital.specialities.slice(0, 3).map((speciality, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                        >
                          {speciality}
                        </span>
                      ))}
                      {hospital.specialities.length > 3 && (
                        <span className="inline-block px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">
                          +{hospital.specialities.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Application ID */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    ID: {hospital.applicationId}
                  </div>
                  <button className="text-teal-600 hover:text-teal-800 text-sm font-medium flex items-center">
                    View Details
                    <Eye className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination or Load More (if needed) */}
        {filteredHospitals.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Showing {filteredHospitals.length} of {hospitals.length} hospitals
            </p>
          </div>
        )}
      </div>

      {/* Hospital Details Modal */}
      {showHospitalModal && selectedHospital && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedHospital.hospitalName}</h3>
                  <p className="text-sm text-gray-600">{selectedHospital.city}, {selectedHospital.state}</p>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(selectedHospital.status)}
                  <button
                    onClick={() => setShowHospitalModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <RefreshCw className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Contact Information</h4>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center text-sm text-gray-900">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {selectedHospital.phone}
                      </div>
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {selectedHospital.email}
                      </div>
                      {selectedHospital.website && (
                        <div className="flex items-center text-sm text-gray-900">
                          <Globe className="h-4 w-4 mr-2 text-gray-400" />
                          <a 
                            href={selectedHospital.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-teal-600 hover:text-teal-800"
                          >
                            Visit Website
                            <ExternalLink className="h-3 w-3 ml-1 inline" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Hospital Details</h4>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center text-sm text-gray-900">
                        <Bed className="h-4 w-4 mr-2 text-gray-400" />
                        {selectedHospital.numberOfBeds} Beds
                      </div>
                      <div className="flex items-center text-sm text-gray-900">
                        <FileText className="h-4 w-4 mr-2 text-gray-400" />
                        Reg: {selectedHospital.registrationNumber}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Application Timeline</h4>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        Referred: {formatDate(selectedHospital.referral?.referredAt)}
                      </div>
                      <div className="flex items-center text-sm text-gray-900">
                        <Activity className="h-4 w-4 mr-2 text-gray-400" />
                        Submitted: {formatDate(selectedHospital.submittedAt)}
                      </div>
                      {selectedHospital.approvedAt && (
                        <div className="flex items-center text-sm text-green-700">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          Approved: {formatDate(selectedHospital.approvedAt)}
                        </div>
                      )}
                      {selectedHospital.rejectedAt && (
                        <div className="flex items-center text-sm text-red-700">
                          <XCircle className="h-4 w-4 mr-2 text-red-500" />
                          Rejected: {formatDate(selectedHospital.rejectedAt)}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedHospital.rejectionReason && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Rejection Reason</h4>
                      <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-800">{selectedHospital.rejectionReason}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Full Address</h4>
                <div className="flex items-start text-sm text-gray-900">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    {selectedHospital.address}, {selectedHospital.city}, {selectedHospital.state} - {selectedHospital.pincode}
                  </div>
                </div>
              </div>

              {/* Facilities */}
              {selectedHospital.facilities && selectedHospital.facilities.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Facilities</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selectedHospital.facilities.map((facility, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                        {facility}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialities */}
              {selectedHospital.specialities && selectedHospital.specialities.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Medical Specialities</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedHospital.specialities.map((speciality, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        {speciality}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Representative Information */}
              {selectedHospital.representative && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Hospital Representative</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedHospital.representative.name}</p>
                        <p className="text-sm text-gray-600">{selectedHospital.representative.designation}</p>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-700">
                          <Phone className="h-3 w-3 mr-2 text-gray-400" />
                          {selectedHospital.representative.phone}
                        </div>
                        <div className="flex items-center text-sm text-gray-700">
                          <Mail className="h-3 w-3 mr-2 text-gray-400" />
                          {selectedHospital.representative.email}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Application ID: {selectedHospital.applicationId}
                </div>
                <button
                  onClick={() => setShowHospitalModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral Link Modal */}
      {showReferralLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-2 rounded-lg mr-3">
                    <Link className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Hospital Referral Link</h3>
                    <p className="text-sm text-gray-600">Share this link with hospitals to onboard them</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReferralLinkModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <RefreshCw className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Referral Link
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm focus:outline-none"
                  />
                  <button
                    onClick={copyReferralLink}
                    className={`px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg transition-colors ${
                      linkCopied 
                        ? 'bg-green-500 text-white border-green-500' 
                        : 'bg-white hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {linkCopied ? (
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Copied!
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </div>
                    )}
                  </button>
                </div>
              </div>
              <a href={referralLink} target='_blank' rel='noopener noreferrer' className="text-teal-600 hover:text-teal-800 text-sm flex items-center mb-4">
                <ExternalLink className="h-4 w-4 mr-1" />
                {"Register Hospital Now"}
              </a>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <Building2 className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">How it works:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Share this link with hospitals you want to onboard</li>
                      <li>• When they click the link, they'll be directed to hospital registration</li>
                      <li>• The registration will automatically link them to your referral account</li>
                      <li>• You'll get credit and commissions for successful referrals</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-amber-900 mb-1">Important Notes:</h4>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li>• This link is unique to your account - don't share your personal details</li>
                      <li>• Hospitals must complete the full registration process</li>
                      <li>• Commission is paid only after hospital approval and activation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Your Lead Generator ID: {userData.leadGeneratorId || userData.uid}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={copyReferralLink}
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </button>
                  <button
                    onClick={() => setShowReferralLinkModal(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadGeneratorHospitals;